/**
 * Named routes for the course editor (Content, Settings, Grading Scheme, etc.).
 * @type {readonly string[]}
 */
export const COURSE_EDIT_ROUTE_NAMES = Object.freeze([
    'course-edit',
    'course-edit-content',
    'course-edit-settings',
    'course-edit-grading-scheme',
    'course-edit-schedule',
    'course-edit-remediation',
    'course-edit-automated-surveys',
    'course-edit-certificate',
]);

/**
 * @param {string|null|undefined} routeName
 * @returns {boolean}
 */
export function isCourseEditRouteName(routeName) {
    return COURSE_EDIT_ROUTE_NAMES.includes(routeName);
}

/**
 * Course "Meetings" is one logical section split across two named routes
 * (the Upcoming / Previous subviews).
 * @type {readonly string[]}
 */
export const COURSE_MEETINGS_ROUTE_NAMES = Object.freeze([
    'course-meetings-view-upcoming',
    'course-meetings-view-previous',
]);

/**
 * @param {string|null|undefined} routeName
 * @returns {boolean}
 */
export function isCourseMeetingsRouteName(routeName) {
    return COURSE_MEETINGS_ROUTE_NAMES.includes(routeName);
}

/**
 * Course "Due Dates and Schedule" is one logical section split across the
 * Due Dates, Events, and Term Schedule Overview tabs (Meetings has its own
 * sidebar item and is intentionally excluded).
 * @type {readonly string[]}
 */
export const COURSE_DUE_DATES_SCHEDULE_ROUTE_NAMES = Object.freeze([
    'course-activities-view-upcoming',
    'course-activities-view-previous',
    'course-time-periods-view-upcoming',
    'course-time-periods-view-previous',
    'course-term-schedule-overview',
]);

/**
 * @param {string|null|undefined} routeName
 * @returns {boolean}
 */
export function isCourseDueDatesScheduleRouteName(routeName) {
    return COURSE_DUE_DATES_SCHEDULE_ROUTE_NAMES.includes(routeName);
}

/**
 * Determine whether a menu route matches the current route exactly.
 *
 * @param {object|null|undefined} targetRoute
 * @param {object} currentRoute
 * @param {{ resolve: (route: object) => { name?: string, params?: object } }} router
 * @returns {boolean}
 */
export function isRouteExactlyActive(targetRoute, currentRoute, router) {
    if (!targetRoute || !currentRoute?.name) {
        return false;
    }

    const resolved = router.resolve(targetRoute);

    if (resolved.name !== currentRoute.name) {
        return false;
    }

    const paramKeys = Object.keys(resolved.params || {});

    if (paramKeys.length === 0) {
        return true;
    }

    return paramKeys.every(key => String(currentRoute.params[key]) === String(resolved.params[key]));
}

/**
 * @param {Array} nodes
 * @param {string} targetKey
 * @returns {boolean}
 */
function nodeTreeContainsKey(nodes, targetKey) {
    return nodes.some(node => {
        if (node.key === targetKey) {
            return true;
        }

        if (node.children?.length) {
            return nodeTreeContainsKey(node.children, targetKey);
        }

        return false;
    });
}

/**
 * @param {Array} topLevelNodes
 * @param {string} activeKey
 * @returns {string|null}
 */
export function findTopLevelSummaryKey(topLevelNodes, activeKey) {
    if (!activeKey) {
        return null;
    }

    const topLevelMatch = topLevelNodes.find(node => node.key === activeKey);

    if (topLevelMatch) {
        return topLevelMatch.key;
    }

    const parentMatch = topLevelNodes.find(
        node => node.children?.length && nodeTreeContainsKey(node.children, activeKey)
    );

    return parentMatch?.key ?? null;
}

/**
 * Find the tree node and ancestor expansion keys that match the current route.
 *
 * @param {Array} nodes
 * @param {object} currentRoute
 * @param {{ resolve: (route: object) => { name?: string, params?: object } }} router
 * @param {string[]} parentKeys
 * @returns {{ selectionKeys: object, expandedKeys: object, activeKey: string|null }|null}
 */
export function findActiveMenuNodeState(nodes, currentRoute, router, parentKeys = []) {
    const activeNode = nodes.find(
        node => node.data?.route && isRouteExactlyActive(node.data.route, currentRoute, router)
    );

    if (activeNode) {
        const expandedKeys = parentKeys.reduce((accumulator, key) => {
            accumulator[key] = true;
            return accumulator;
        }, {});

        return {
            selectionKeys: { [activeNode.key]: true },
            expandedKeys,
            activeKey: activeNode.key,
        };
    }

    return nodes.reduce((matchedState, node) => {
        if (matchedState || !node.children?.length) {
            return matchedState;
        }

        return findActiveMenuNodeState(node.children, currentRoute, router, [...parentKeys, node.key]);
    }, null);
}

/**
 * Resolve sidebar highlight state for the current route.
 *
 * @param {Array} topLevelNodes
 * @param {object} currentRoute
 * @param {{ resolve: (route: object) => { name?: string, params?: object } }} router
 * @returns {{ selectionKeys: object, expandedKeys: object, activeSummaryKey: string|null }}
 */
export function resolveSidebarMenuActiveState(topLevelNodes, currentRoute, router) {
    const matchedState = findActiveMenuNodeState(topLevelNodes, currentRoute, router);

    if (matchedState) {
        return {
            selectionKeys: matchedState.selectionKeys,
            expandedKeys: matchedState.expandedKeys,
            activeSummaryKey: findTopLevelSummaryKey(topLevelNodes, matchedState.activeKey),
        };
    }

    if (currentRoute.name === 'home') {
        return {
            selectionKeys: { home: true },
            expandedKeys: {},
            activeSummaryKey: 'home',
        };
    }

    return {
        selectionKeys: {},
        expandedKeys: {},
        activeSummaryKey: null,
    };
}

/**
 * Routes where the learner is taking a locked-down attempt. The course
 * sidebar must stay hidden here. Do not match by `includes('attempt')` —
 * that also matches Attempt Authorizations and Manage Quiz Attempts.
 *
 * @type {readonly string[]}
 */
export const ATTEMPT_LOCKDOWN_ROUTE_NAMES = Object.freeze([
    'quiz-attempt',
    'survey-attempt',
    'automated-survey-attempt',
    'identity-verification-attempt',
]);

/**
 * Global Quiz Dashboard plus the course-scoped alias used from a launched
 * course so the course sidebar keeps its `:courseId` context.
 *
 * @type {readonly string[]}
 */
export const QUIZ_DASHBOARD_ROUTE_NAMES = Object.freeze(['quiz-dashboard', 'course-quiz-dashboard']);

export function isAttemptLockdownRouteName(routeName) {
    return ATTEMPT_LOCKDOWN_ROUTE_NAMES.includes(routeName);
}

export function isQuizDashboardRouteName(routeName) {
    return QUIZ_DASHBOARD_ROUTE_NAMES.includes(routeName);
}
